import { useEffect } from 'react';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import { AppFormItem, ConditionalRender } from 'components/atoms';
import {
    FormNavButtons,
    IsFreeHoursPickerElement,
    IsTimeIntervalPickerElement,
    MealPaymentTypePickerElement,
    CurrencyInputElement,
    SimpleTimePickerElement,
    SimpleInputNumberElement,
    SimpleSelectElement,
} from 'components/molecules';
import { AppForm, AppFormProps } from 'components/organisms';
import { formFields } from 'consts/form/formFields';
import { TIME_LABEL } from 'consts/labels/common';
import { commonTexts, settingsTexts } from 'consts/text';
import { useForm, useWatch } from 'rc-field-form';
import { MethodOfCalcTheFee, SettlementStaysFormFields } from 'types';
import { SHORT_TIME_FORMAT } from 'consts/timeFormats';
import { DAY_HOURS_NUMBER, THIRTY_MINUTES } from 'consts/timeConsts';
import { timeRangeInMinutesEqualLimitOrLessThanLimitBy30MinutesValidator } from 'utils/validators';

export type SettlementStaysEditFormProps = Omit<
    AppFormProps<SettlementStaysFormFields>,
    'width' | 'title' | 'name' | 'onFinish' | 'error' | 'withGoBack'
> & {
    axiosError?: AxiosError | null;
    isLoading?: boolean;
    onFinish: (settlementsStayData: SettlementStaysFormFields) => void;
};

const ONE_HOUR = 1;

export const SettlementStaysEditForm = ({
    form,
    axiosError,
    initialValues,
    ...props
}: SettlementStaysEditFormProps) => {
    const [formInstance] = useForm(form);
    const methodOfCalcTheFeeValue = useWatch<MethodOfCalcTheFee>(
        formFields.paymentType,
        formInstance,
    );
    const isFreeHours = useWatch<boolean>(formFields.freeHours, formInstance);
    const isFreeHoursTimeFrame = useWatch<boolean>(formFields.freeHoursTimeFrame, formInstance);
    const stayTimeType = useWatch<string>(formFields.stayTimeType, formInstance);
    const stayFeeType = useWatch<string>(formFields.stayFeeType, formInstance);

    const isMonthlyMethodOfCalTheFee = methodOfCalcTheFeeValue === MethodOfCalcTheFee.MONTHLY;
    const isHourlyMethodOfCalTheFee = methodOfCalcTheFeeValue === MethodOfCalcTheFee.HOURLY;
    const settlementStaysEditError = axiosError ? commonTexts.errorMessages.unknown : '';

    useEffect(() => {
        if (methodOfCalcTheFeeValue === MethodOfCalcTheFee.MONTHLY) {
            formInstance.setFieldsValue({
                freeHours: undefined,
                freeHoursAmount: undefined,
                freePeriodStart: undefined,
                freePeriodEnd: undefined,
                freeHoursTimeFrame: undefined,
                workingHours: undefined,
            });
        }
    }, [formInstance, methodOfCalcTheFeeValue]);

    useEffect(() => {
        if (!isFreeHoursTimeFrame) {
            formInstance.setFieldsValue({
                freePeriodStart: undefined,
                freePeriodEnd: undefined,
            });
        }
    }, [formInstance, isFreeHoursTimeFrame]);

    useEffect(() => {
        if (!isFreeHours) {
            formInstance.setFieldsValue({
                freeHoursAmount: undefined,
                freeHoursTimeFrame: undefined,
            });
        }
    }, [formInstance, isFreeHours]);

    return (
        <AppForm
            withHeader={false}
            form={form}
            name="settlementStaysEdit"
            error={settlementStaysEditError}
            {...props}
        >
            <MealPaymentTypePickerElement name={formFields.paymentType} />

            {initialValues && (
                <>
                    <SimpleSelectElement
                        label="Typ opłaty za pobyt"
                        name={formFields.stayFeeType}
                        options={[
                            { label: 'Na podstawie obecności', value: 'ATTENDANCE_BASED' },
                            { label: 'Stała opłata', value: 'FLAT_MONTHLY' },
                        ]}
                    />
                    <ConditionalRender condition={stayFeeType === 'FLAT_MONTHLY'}>
                        <CurrencyInputElement
                            label="Stała opłata miesięczna"
                            name={formFields.flatMonthlyFee}
                        />
                    </ConditionalRender>
                    <ConditionalRender condition={stayFeeType === 'ATTENDANCE_BASED'}>
                        <CurrencyInputElement
                            label="Koszt za godzinę"
                            name={formFields.costPerHour}
                        />
                    </ConditionalRender>
                    <ConditionalRender condition={stayFeeType === 'ATTENDANCE_BASED'}>
                        <SimpleSelectElement
                            label="Typ czasu pobytu"
                            name={formFields.stayTimeType}
                            options={[
                                { label: 'Darmowe pierwsze X godzin', value: 'FREE_FIRST_X_HOURS' },
                                { label: 'Darmowe w przedziale czasowym', value: 'FREE_PERIOD' },
                            ]}
                        />
                        <ConditionalRender condition={stayTimeType === 'FREE_FIRST_X_HOURS'}>
                            <SimpleInputNumberElement
                                label="Liczba darmowych godzin"
                                name={formFields.freeHoursAmount}
                                precision={0}
                            />
                        </ConditionalRender>
                        <ConditionalRender condition={stayTimeType === 'FREE_PERIOD'}>
                            <AppFormItem justifyContent="space-between" doubleItem>
                                <SimpleTimePickerElement
                                    mainLabelText="Czas od"
                                    additionalLabelText={TIME_LABEL}
                                    name={formFields.freePeriodStart}
                                    format={SHORT_TIME_FORMAT}
                                    minuteStep={THIRTY_MINUTES}
                                />
                                <SimpleTimePickerElement
                                    mainLabelText="Czas do"
                                    additionalLabelText={TIME_LABEL}
                                    name={formFields.freePeriodEnd}
                                    format={SHORT_TIME_FORMAT}
                                    minuteStep={THIRTY_MINUTES}
                                />
                            </AppFormItem>
                        </ConditionalRender>
                        <SimpleInputNumberElement
                            label="Zaokrąglanie czasu od (minuty)"
                            name={formFields.timeRoundingUnitMinutes}
                            precision={0}
                        />
                    </ConditionalRender>
                </>
            )}
            <ConditionalRender condition={isMonthlyMethodOfCalTheFee}>
                <CurrencyInputElement
                    label={settingsTexts.settlements.stays.formLabels.tuitionLabel}
                    name={formFields.price}
                />
            </ConditionalRender>
            <ConditionalRender condition={isHourlyMethodOfCalTheFee}>
                <IsFreeHoursPickerElement />
            </ConditionalRender>
            <ConditionalRender condition={isFreeHours}>
                <SimpleInputNumberElement
                    name={formFields.freeHoursAmount}
                    label={settingsTexts.settlements.stays.formLabels.freeHoursAmountLabel}
                    additionalText={
                        settingsTexts.settlements.stays.formLabels.hoursNumberPerDayAdditionalText
                    }
                    hasOptionalText
                    precision={0}
                    rules={[
                        {
                            type: 'number',
                            min: ONE_HOUR,
                            max: DAY_HOURS_NUMBER - ONE_HOUR,
                            message:
                                settingsTexts.settlements.stays.formValidationMessages
                                    .invalidFreeHoursNumber,
                        },
                    ]}
                />
                <IsTimeIntervalPickerElement />
            </ConditionalRender>
            <ConditionalRender condition={isFreeHoursTimeFrame}>
                <AppFormItem justifyContent="space-between" doubleItem>
                    <SimpleTimePickerElement
                        mainLabelText={settingsTexts.settlements.stays.formLabels.timeFromLabel}
                        additionalLabelText={TIME_LABEL}
                        name={formFields.freePeriodStart}
                        format={SHORT_TIME_FORMAT}
                        minuteStep={THIRTY_MINUTES}
                    />
                    <SimpleTimePickerElement
                        mainLabelText={settingsTexts.settlements.stays.formLabels.timeToLabel}
                        additionalLabelText={TIME_LABEL}
                        name={formFields.freePeriodEnd}
                        format={SHORT_TIME_FORMAT}
                        minuteStep={THIRTY_MINUTES}
                        dependencies={[formFields.freeHoursAmount, formFields.freePeriodStart]}
                        rules={[
                            ({ getFieldValue }) =>
                                timeRangeInMinutesEqualLimitOrLessThanLimitBy30MinutesValidator(
                                    getFieldValue,
                                    {
                                        timeStartValueFieldName: formFields.freePeriodStart,
                                        timeLimitValueInHoursFieldName: formFields.freeHoursAmount,
                                        isTimeRangeNotWithinLimitErrorText:
                                            settingsTexts.settlements.stays.formValidationMessages
                                                .freeHoursTimeFrameDurationNotEqualFreeHoursNumber,
                                    },
                                ),
                        ]}
                    />
                </AppFormItem>
            </ConditionalRender>
            <ConditionalRender condition={isHourlyMethodOfCalTheFee}>
                <CurrencyInputElement
                    label={settingsTexts.settlements.stays.formLabels.chargeAmountLabel}
                    name={formFields.price}
                />
            </ConditionalRender>
            <FormNavButtons
                formValuesBeforeChanges={initialValues}
                buttonText={commonTexts.actionLabels.save}
                leftIcon={<IconDeviceFloppy size={20} />}
            />
        </AppForm>
    );
};
